// ============================================
// Kuwahara (Oil Painting) Post-Process Effect
// ============================================
// Generalized Kuwahara filter — flattens gradients into painterly patches
// while keeping edges crisp (the sector-variance trick).

import type { IRenderContext } from '../../core/IRenderContext';
import {
  PostProcessEffect,
  type PostProcessContext,
  type PostProcessParameter,
} from '../PostProcessStack';
import { KUWAHARA_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Kuwahara post-processing effect (oil painting look)
 */
export class KuwaharaEffect extends PostProcessEffect {
  readonly type = 'kuwahara';
  readonly displayName = 'Oil Painting';

  // Parameters
  radius = 4;        // Window radius in pixels (bigger = broader strokes, more expensive)
  sharpness = 8.0;   // Variance exponent — higher snaps harder to the flattest sector

  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = KUWAHARA_LAYOUT.createWriter();

  getParameters(): PostProcessParameter[] {
    return [
      { name: 'radius', type: 'int', min: 1, max: 8, default: 4, description: 'Brush radius (px)' },
      { name: 'sharpness', type: 'float', min: 1, max: 18, default: 8.0, description: 'Sector hardness' },
    ];
  }

  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'radius': return this.radius;
      case 'sharpness': return this.sharpness;
      default: return 0;
    }
  }

  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'radius': this.radius = Math.floor(Math.max(1, Math.min(8, value))); break;
      case 'sharpness': this.sharpness = Math.max(0.5, Math.min(18, value)); break;
    }
  }

  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();

    const kuwaharaWgsl = getCompiledShader('postprocess/kuwahara');
    this.shader = ctx.createShader(kuwaharaWgsl, kuwaharaWgsl);

    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'kuwahara-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'kuwahara-ldr',
    });
  }

  protected onRender(context: PostProcessContext): void {
    const { ctx, source, intensity, isHdrTarget } = context;

    // NOTE: PostProcessStack already set render target and viewport

    this.uniformWriter.setAll({
      radius: this.radius,
      sharpness: this.sharpness,
      // The effect/stack intensity doubles as the wet-paint mix with the original.
      blend: intensity,
      _pad: 0,
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }

  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
